import React, { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import { updateTitle, updateCategory, updateSlug, updateContent, updateBlogImage } from "../redux/blog/actions";
import { useParams, useNavigate } from "react-router-dom";

const BASE_URL = process.env.REACT_APP_BASE_URL || "http://localhost:5000";

// Register TableBlot so Quill preserves <table> tags when loading/editing content
const BlockEmbed = Quill.import("blots/block/embed");
let TableBlotRegistered = false;
if (!TableBlotRegistered) {
  class TableBlot extends BlockEmbed {
    static create(value) {
      const node = super.create();
      node.innerHTML = value;
      return node;
    }
    static value(node) {
      return node.innerHTML;
    }
  }
  TableBlot.blotName = "table";
  TableBlot.tagName = "table";
  try { Quill.register(TableBlot); } catch (_) {}
  TableBlotRegistered = true;
}

export default function EditBlog() {
  const quillRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const { title, category, slug, content, blogImage } = useSelector((state) => state.blog);

  useEffect(() => {
    fetch(`${BASE_URL}/api/blog/getposts?postId=${id}`)
      .then((res) => res.json())
      .then((data) => {
        if (data && data.length > 0) {
          const blogData = data[0];
          dispatch(updateTitle(blogData.title));
          dispatch(updateCategory(blogData.category));
          dispatch(updateSlug(blogData.slug));
          dispatch(updateContent(blogData.content));
          dispatch(updateBlogImage(blogData.featureImage));
        }
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to fetch blog data");
        setLoading(false);
      });
  }, [id, dispatch]);

  const getSignedUrl = async (file) => {
    const res = await fetch(`${BASE_URL}/api/blog/getuploadurl`);
    const data = await res.json();
    await fetch(data.url, { method: "PUT", body: file, headers: { "Content-Type": file.type } });
    return `https://beta-website-images.s3.ap-south-1.amazonaws.com/${data.key}`;
  };

  const handleBlogImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith("image/")) {
      try {
        setLoading(true);
        const imageURL = await getSignedUrl(file);
        dispatch(updateBlogImage(imageURL));
      } catch {
        setError("Failed to upload image");
      } finally {
        setLoading(false);
      }
    } else {
      setError("Please upload a valid image file.");
    }
  };

  // Direct S3 image upload from editor toolbar
  const imageHandler = useCallback(async () => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();
    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;
      try {
        const imageURL = await getSignedUrl(file);
        const quill = quillRef.current.getEditor();
        const range = quill.getSelection(true);
        quill.insertEmbed(range ? range.index : 0, "image", imageURL);
        quill.setSelection((range ? range.index : 0) + 1);
      } catch {
        console.error("Image upload failed");
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!window.confirm("Are you sure you want to update this blog?")) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BASE_URL}/api/blog/updatepost/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title, category, slug, content, image: blogImage }),
      });
      if (res.ok) {
        setSuccessMessage("Blog has been updated successfully!");
        setTimeout(() => navigate(`/blog/${id}`), 2000);
      } else {
        throw new Error("Failed to update blog");
      }
    } catch {
      setError("Failed to update blog");
    } finally {
      setLoading(false);
    }
  };

  const modules = {
    toolbar: {
      container: [
        [{ header: [1, 2, 3, 4, 5, 6, false] }, { font: [] }],
        [{ size: [] }],
        ["bold", "italic", "underline", "strike", "blockquote", "code-block"],
        [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
        [{ color: [] }, { background: [] }],
        [{ align: "" }, { align: "center" }, { align: "right" }, { align: "justify" }],
        ["link", "image", "video"],
        ["clean"],
      ],
      handlers: { image: imageHandler },
    },
    clipboard: { matchVisual: false },
  };

  const formats = [
    "header", "font", "size", "bold", "italic", "underline", "strike",
    "blockquote", "list", "bullet", "indent", "link", "image", "video",
    "align", "color", "background", "code-block", "table",
  ];

  if (loading) return <div className="text-center">Loading...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="container">
      {successMessage && <div className="success">{successMessage}</div>}
      <div className="blogadminform">

        <div>
          <h6 className="mt-2">Blog Title</h6>
          <input type="text" className="formitem" placeholder="Blog Title"
            onChange={(e) => dispatch(updateTitle(e.target.value))} value={title} />
        </div>

        <div>
          <h6 className="mt-2">Category</h6>
          <select className="custom-select formitem" onChange={(e) => dispatch(updateCategory(e.target.value))} value={category}>
            <option value="software">Software</option>
            <option value="devops">Devops</option>
            <option value="ai-ml">AI/ML</option>
            <option value="iot">IOT</option>
            <option value="ar">AR</option>
          </select>
        </div>

        <div>
          <h6 className="mt-2">Enter Slug</h6>
          <input type="text" className="formitem" placeholder="Enter the Slug"
            onChange={(e) => dispatch(updateSlug(e.target.value))} value={slug} />
        </div>

        {/* Table Insert UI — commented out
        <div style={{ background: "#f0fff4", padding: "12px", borderRadius: "6px", border: "1px solid #bbf7d0", marginBottom: "12px" }}>
          <h6 style={{ color: "#166534", marginBottom: "8px" }}>Insert Table into Blog Content</h6>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            <div>
              <label className="small me-1">Rows:</label>
              <input type="number" min="1" max="20" value={tableRows} onChange={(e) => setTableRows(Number(e.target.value))}
                style={{ width: "60px", borderRadius: "4px", border: "1px solid #d0d5dd", padding: "4px 6px" }} />
            </div>
            <div>
              <label className="small me-1">Columns:</label>
              <input type="number" min="1" max="20" value={tableCols} onChange={(e) => setTableCols(Number(e.target.value))}
                style={{ width: "60px", borderRadius: "4px", border: "1px solid #d0d5dd", padding: "4px 6px" }} />
            </div>
            <button type="button" onClick={insertTable}
              style={{ background: "#166534", color: "white", border: "none", borderRadius: "6px", padding: "6px 16px", cursor: "pointer" }}>
              + Insert Table
            </button>
          </div>
          <div style={{ color: "#6b7280", fontSize: "12px", marginTop: "6px" }}>
            Click inside the editor first, then click "Insert Table".
          </div>
        </div>
        */}

        <div>
          <h6 className="mt-2">Enter Content</h6>
          <div style={{ background: "#f0f7ff", padding: "10px", borderRadius: "6px", fontSize: "13px", color: "#1e3a8a", marginBottom: "8px" }}>
            Use the 🖼️ image button in the toolbar to upload &amp; insert images directly into content.
          </div>
          <div className="blog-content">
            <ReactQuill ref={quillRef} theme="snow" modules={modules} formats={formats}
              onChange={(value) => dispatch(updateContent(value))} value={content} />
          </div>
        </div>

        <div>
          <h6 className="mt-2">Upload Blog Feature Image</h6>
          <input type="file" onChange={handleBlogImageChange} />
          {blogImage && (
            <div>
              <h6 className="mt-2">Preview:</h6>
              <img src={blogImage} alt="Uploaded" style={{ maxWidth: "400px", maxHeight: "400px" }} />
            </div>
          )}
        </div>

        <button onClick={handleSubmit} className="formbtn">Update</button>
      </div>
    </div>
  );
}
